import { useState } from "react";
import { paceGames } from "../../../../engine/lookahead";
import { startWaitMs } from "../../../../engine/plan";
import { owedScores } from "../../../../engine/selectors";
import type { PlayerId, TournamentState } from "../../../../engine/types";
import { gamesDone, windDownChoices, windDownNow, type WindDownPlan } from "../../../../engine/winddown";
import { ConfirmButton, clockTime, nameOf, useOpened, type TabProps } from "./common";
import { afterWhistle } from "./whistle";

/** "Luke", "Luke and Raul", "Luke, Raul and Sam". */
function names(state: TournamentState, ids: PlayerId[]): string {
  const all = ids.map((id) => nameOf(state, id));
  return all.length < 2 ? (all[0] ?? "") : `${all.slice(0, -1).join(", ")} and ${all[all.length - 1]}`;
}

const games = (n: number): string => `${n} ${n === 1 ? "game" : "games"}`;
const roundsWord = (n: number): string => `${n} more ${n === 1 ? "round" : "rounds"}`;

/** "everyone ends on 8 games" or "everyone ends on 9 games except Luke and Raul, who end on 8". */
export function endsOn(state: TournamentState, plan: WindDownPlan): string {
  if (!plan.short.length) return `everyone ends on ${games(plan.target)}`;
  const who = names(state, plan.short);
  return `everyone ends on ${games(plan.target)} except ${who}, who ${plan.short.length === 1 ? "ends" : "end"} on ${plan.target - 1}`;
}

/** What the games are when they are done: "everyone here has 8 games", and who has one fewer. */
export function endedOn(state: TournamentState, plan: WindDownPlan): string {
  if (!plan.short.length) return `everyone here has ${games(plan.target)}`;
  const who = names(state, plan.short);
  return `everyone here has ${games(plan.target)} except ${who}, who ${plan.short.length === 1 ? "has" : "have"} ${plan.target - 1}`;
}

/** "Round 15 on 4 courts, round 16 on 2 courts.", and "Rounds 15 to 18 on 4 courts" for a run of the same. */
export function roundsLine(firstNo: number, plan: WindDownPlan): string {
  const runs: { from: number; to: number; courts: number }[] = [];
  plan.rounds.forEach((players, i) => {
    const last = runs[runs.length - 1];
    if (last && last.courts === players / 4) last.to = firstNo + i;
    else runs.push({ from: firstNo + i, to: firstNo + i, courts: players / 4 });
  });
  const parts = runs.map((r, i) => {
    const which = r.from === r.to ? `round ${r.from}` : `rounds ${r.from} to ${r.to}`;
    const said = i ? which : which[0]!.toUpperCase() + which.slice(1);
    return `${said} on ${r.courts} ${r.courts === 1 ? "court" : "courts"}`;
  });
  return `${parts.join(", ")}.`;
}

/** "round 8 court 3; round 9 courts 1, 2": what is owed, a round at a time. */
export function owedWords(owed: { roundNo: number; court: number }[]): string {
  const by = new Map<number, number[]>();
  for (const o of owed) by.set(o.roundNo, [...(by.get(o.roundNo) ?? []), o.court]);
  return [...by].map(([no, courts]) => `round ${no} ${courts.length === 1 ? "court" : "courts"} ${courts.join(", ")}`).join("; ");
}

/**
 * Once the last wind-down round is over. The wind down counts the games drawn and the board the
 * games scored, so while a score is owed the two would disagree: it says which score first.
 */
export function doneLine(state: TournamentState, plan: WindDownPlan, owed: { roundNo: number; court: number }[]): string {
  if (!owed.length) return `The games are done: ${endedOn(state, plan)}.`;
  return `The rounds are done. Once the ${owed.length === 1 ? "score" : "scores"} for ${owedWords(owed)} ${owed.length === 1 ? "is" : "are"} in, ${endedOn(state, plan)}.`;
}

/**
 * "The last of them ends about 18:05.": at the pace the rounds have been going, the wait for Start
 * included, and a warning when that is after the last whistle.
 */
function endsAbout(state: TournamentState, plan: WindDownPlan, now: number): string {
  if (!plan.rounds.length) return "";
  const { gameMs, changeoverMs } = state.config;
  const wait = startWaitMs(state);
  const { phase, phaseEndsAt } = state.clock;
  const next = phase === "playing"
    ? (phaseEndsAt ?? now) + changeoverMs + wait
    : phase === "changeover" ? (phaseEndsAt ?? now) + wait : now;
  const end = Math.max(next, now) + (plan.rounds.length - 1) * (gameMs + changeoverMs + wait) + gameMs;
  return `The last of them ends about ${clockTime(end)}.${afterWhistle(end, state.config.lastWhistleAt)}`;
}

/** The sum in one sentence: "2 more rounds: everyone ends on 8 games." */
function sum(state: TournamentState, plan: WindDownPlan): string {
  if (!plan.rounds.length) return `No more rounds needed: ${endsOn(state, plan)}.`;
  return `${roundsWord(plan.rounds.length)}: ${endsOn(state, plan)}.`;
}

/**
 * The end of the day (decision 42): how many games each the day reaches at this pace, and the
 * wind down. Wind down opens a preview of the sum; confirming it starts it, and from then on the
 * card says where it stands, what changed, and when the games are done.
 */
export function WindDownCard({ t, state }: Pick<TabProps, "t" | "state">) {
  const [preview, setPreview] = useState(false);
  // the preview's keys are a decision: the undo bar and a toast move off them while it is open
  const box = useOpened<HTMLDivElement>(preview);
  const now = t.now();
  const pace = paceGames(state, now);
  const playing = state.clock.phase === "playing";
  const nextNo = state.rounds.length + 1;
  const paceLine = pace
    ? `At this pace, about ${games(pace.games)} each by ${clockTime(pace.by)}.`
    : state.config.lastWhistleAt === null
      ? "Set the last whistle in Settings for the day to see how many games each the day reaches."
      : null;

  if (state.windDown) {
    const plan = windDownNow(state);
    const was = state.windDown.first;
    const wasShort = was.short ?? [];
    // a new target, or other people (or none) ending one short: either way it is not what was confirmed
    const changed = plan !== null && (plan.target !== was.target || plan.short.join() !== wasShort.join());
    const wasWords = wasShort.length
      ? `everyone on ${games(was.target)} except ${names(state, wasShort)}, on ${was.target - 1}`
      : `everyone on ${games(was.target)}`;
    const owed = owedScores(state);
    return (
      <section className="ad-card ad-winddown on">
        <h2>Winding down</h2>
        {!plan ? (
          <p className="ad-pace">There are not enough people here to even out the games. Cancel the wind down to carry on as usual.</p>
        ) : gamesDone(state) && !playing ? (
          <>
            <p className="ad-pace">{doneLine(state, plan, owed)}</p>
            <p className="ad-note">No more rounds are drawn. The podium and the awards are on the More tab, under Reveal.</p>
          </>
        ) : !plan.rounds.length ? (
          <p className="ad-pace">Round {state.rounds.length} is the last: when it ends, {endsOn(state, plan)}.</p>
        ) : (
          <>
            <p className="ad-pace">{sum(state, plan)}</p>
            <p className="ad-note">{roundsLine(nextNo, plan)}{playing ? ` After round ${state.rounds.length}, being played now.` : ""} {endsAbout(state, plan, now)}</p>
          </>
        )}
        {changed && <p className="ad-note"><b>The sum changed</b> as people came or went: it was {wasWords}.</p>}
        <p className="ad-note">The draw takes whoever is furthest short. Courts can sit empty, and someone can sit out three rounds running.</p>
        <ConfirmButton label="Cancel wind down" className="btn quiet ad-wide" yes="Cancel it"
          question="The next round is drawn the usual way again, with the usual rest and mix."
          onConfirm={() => t.send({ type: "winddown.set", on: false })} />
      </section>
    );
  }

  const { level, near } = preview ? windDownChoices(state) : { level: null, near: null };
  const start = (isNear: boolean) => {
    t.send({ type: "winddown.set", on: true, ...(isNear ? { near: true } : {}) });
    setPreview(false);
  };
  return (
    <section className="ad-card ad-winddown">
      <h2>The end of the day</h2>
      {paceLine && <p className="ad-pace">{paceLine}</p>}
      {!preview ? (
        <>
          <button type="button" className="btn ad-wide" onClick={() => setPreview(true)}>Wind down</button>
          <p className="ad-note">Works out the fewest rounds that leave everyone here on the same number of games, and shows them before anything changes.</p>
        </>
      ) : (
        // ad-lunch-form is one of the things the floats keep off (Admin.tsx OPENED), as the lunch form is
        <div className="ad-lunch ad-lunch-form ad-winddown-preview" role="group" aria-label="Wind down" ref={box}>
          {!level && !near ? (
            <p>There are not enough people here to even out the games.</p>
          ) : (
            <>
              {level && (
                <>
                  <b>{sum(state, level)}</b>
                  {level.rounds.length > 0 && <span>{roundsLine(nextNo, level)}{playing ? ` After round ${state.rounds.length}, being played now.` : ""} {endsAbout(state, level, now)}</span>}
                  <button type="button" className="btn accent ad-wide" onClick={() => start(false)}>
                    Start the wind down
                  </button>
                </>
              )}
              {near && (
                <>
                  <b>{level ? "Or " : ""}{level ? sum(state, near).replace(/^./, (c) => c.toLowerCase()) : sum(state, near)}</b>
                  {near.rounds.length > 0 && <span>{roundsLine(nextNo, near)} {endsAbout(state, near, now)}</span>}
                  {!level && <span>Everyone level cannot be done with this many here, so this is the closest.</span>}
                  <button type="button" className={"btn ad-wide" + (level ? "" : " accent")} onClick={() => start(true)}>
                    {!level ? "Start the wind down" : near.rounds.length ? `Wind down in ${near.rounds.length} ${near.rounds.length === 1 ? "round" : "rounds"}` : "Stop the rounds here"}
                  </button>
                </>
              )}
              <span className="ad-note">The draw takes whoever is furthest short, fewest games first. Some courts may sit empty. People arriving, leaving or going on a break change the sum, and it shows here.</span>
            </>
          )}
          <button type="button" className="btn quiet ad-wide" onClick={() => setPreview(false)}>Cancel</button>
        </div>
      )}
    </section>
  );
}
